import React, { useEffect, useState } from "react";
import "./DataBuilder.css";
import { v4 } from "uuid";
import { Row, Col, Select, Button, Input, Typography, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { returnDataArrayObject } from "../../../../../utils/arrayHelper";
import NotFound from "../../../../../components/NotFound";
import {
  updateAggRowBuilders,
  updateAggColBuilders,
  updateSelectedFields,
} from "../../../../../redux/editorSlice";
import ColBuilder from "./ColBuilder";
import DeleteIcon from "../../../../../assets/icons/delete-icon.svg";

const { Option } = Select;

export default function DataBuilder(props) {
  const [filters, setFilters] = useState([]);
  const [fields, setFields] = useState([]);
  const [activeTab, setActiveTab] = useState("row");
  // ColBuilder keeps its own editing state; this mirrors it so the shared Apply can send it
  const [colBuilders, setColBuilders] = useState([]);
  const dispatch = useDispatch();

  // const setFilterItem = () => {
  //   setFilters((f) => [
  //     ...f,
  //     {
  //       id: v4(),
  //       dataType: "text",
  //       aggType: "sum",
  //     },
  //   ]);
  // };
  // one empty row builder - the panel always shows at least this
  const makeBlankRowBuilder = () => ({
    id: v4(),
    displayName: "",
    aggregates: [{ id: v4(), aggType: "sum", fieldName: "" }],
  });

  const setFilterItem = () => {
    setFilters((f) => [...f, makeBlankRowBuilder()]);
  };

  const addAggregate = (filterId) => {
    setFilters(
      filters.map((f) =>
        f.id === filterId
          ? {
              ...f,
              // the whole row shares one function, so inherit it rather than resetting to sum
              aggregates: [...f.aggregates, { id: v4(), aggType: f.aggregates[0]?.aggType || "sum", fieldName: "" }],
            }
          : f,
      ),
    );
  };

  const removeAggregate = (filterId, aggId) => {
    setFilters(
      filters.map((f) => (f.id === filterId ? { ...f, aggregates: f.aggregates.filter((a) => a.id !== aggId) } : f)),
    );
  };

  const updateFilterField = (filterId, key, val) => {
    setFilters(filters.map((f) => (f.id === filterId ? { ...f, [key]: val } : f)));
  };

  const updateAggregate = (filterId, aggId, key, val) => {
    setFilters(
      filters.map((f) =>
        f.id === filterId
          ? {
              ...f,
              aggregates: f.aggregates.map((a) =>
                // aggType is a property of the row: changing it applies to every line,
                // otherwise the locked lines would still submit their old function
                key === "aggType" ? { ...a, aggType: val } : a.id === aggId ? { ...a, [key]: val } : a,
              ),
            }
          : f,
      ),
    );
  };

  /* Removing the last one leaves an empty builder rather than nothing, so the panel is
     never a blank space with only "+ Add New" in it. */
  const deleteFilterItem = (item) => {
    const remaining = filters.filter((f) => f.id !== item.id);
    setFilters(remaining.length ? remaining : [makeBlankRowBuilder()]);
  };

  const updateFilter = (item, key, val) => {
    let updatedFilter = filters.map((f) => {
      return f.id === item.id
        ? {
            id: f.id,
            displayName: key === "displayName" ? val : f.displayName,
            aggType: key === "aggType" ? val : f.aggType,
            fieldName: key === "fieldName" ? val : f.fieldName,
          }
        : { ...f };
    });
    setFilters(updatedFilter);
  };

  // const applyFilters = (filterArray) => {
  //   dispatch(updateAggRowBuilders(filterArray));
  //   message.info("Builders have been successfully applied!");
  // };
  const applyFilters = (filterArray) => {
    /* The panel always holds at least one builder, so an untouched blank one must not be
       submitted - it would add a nameless summary row with nothing in it. */
    const flattened = filterArray.flatMap((f) =>
      (f.aggregates || [])
        .filter((a) => a.fieldName)
        .map((a) => ({
          id: f.id,
          displayName: f.displayName,
          aggType: a.aggType,
          fieldName: a.fieldName,
        })),
    );

    if (!flattened.length) {
      message.warning("Select a field to aggregate before applying");
      return;
    }

    dispatch(updateAggRowBuilders(flattened));
    message.info("Builders have been successfully applied!");
  };

  // back to one empty builder, not to nothing
  const clearBuilder = () => {
    setFilters([makeBlankRowBuilder()]);
  };

  const applyActiveBuilders = () => {
    if (activeTab === "col") {
      /* Same rule as the row tab: the panel always holds one builder, so an untouched
         blank one must not be submitted as a nameless column with no operation. */
      const usable = (colBuilders || []).filter(
        (b) => b.displayName && (b.rowAggregation || []).some((ra) => ra.operator && ra.operator !== "none"),
      );
      if (!usable.length) {
        message.warning("Enter a display name and choose an operation before applying");
        return;
      }
      dispatch(updateAggColBuilders(usable));
      message.info("Builders have been successfully applied!");
      return;
    }
    applyFilters(filters);
  };

  /* Reset returns the widget to "no builders": it clears the editing panel AND the
     applied config, so the summary rows / computed columns disappear from the table.
     Each tab clears only its own side. */
  const resetActiveBuilders = () => {
    if (activeTab === "col") {
      /* ColBuilder rebuilds its own panel from aggregation.row, so clearing the applied
         config is what puts a single empty builder back on screen. */
      setColBuilders([]);
      dispatch(updateAggColBuilders([]));
      message.info("Column builders cleared");
      return;
    }
    clearBuilder();
    dispatch(updateAggRowBuilders([]));
    message.info("Row builders cleared");
  };

  useEffect(() => {
    // [] fallback: before the resource's data arrives there are no availableFields yet,
    // and pushing onto undefined threw, leaving the Field Name dropdown empty
    let availableFieldsSet = props?.selectedResource?.availableFields?.map((af) => af) || [];

    props?.selectedResource?.aggregation?.row?.forEach((a, k) => {
      availableFieldsSet.push({
        fieldName: a.displayName,
        checked: true,
        id: Math.floor(Math.random() * 10000) + 1,
        dataType: "number",
      });
    });
    setFields(availableFieldsSet);

    if (props?.selectedResource?.aggregation?.row && props?.selectedResource?.aggregation?.row.length > 0) {
      setFilters([]);
    }

    // const rows = props.selectedResource?.aggRowBuilders || [];
    // if (rows.length > 0) {
    //   let currentBuilders = rows.map((bd) => {
    //     return {
    //       id: bd.id,
    //       displayName: bd.displayName,
    //       fieldName: bd.fieldName,
    //       aggType: bd.aggType,
    //     };
    //   });
    //   setFilters(currentBuilders);
    // }
    /* Rebuild the panel from the applied config. Entries are stored flattened - one per
       aggregate - so they are regrouped by builder id, otherwise a row with three
       aggregates came back as three separate rows. */
    const rows = props.selectedResource?.aggRowBuilders || [];
    if (rows.length > 0) {
      const grouped = [];
      rows.forEach((bd) => {
        const aggregate = { id: v4(), aggType: bd.aggType, fieldName: bd.fieldName };
        const existing = grouped.find((g) => g.id === bd.id);
        if (existing) {
          existing.aggregates.push(aggregate);
          return;
        }
        grouped.push({ id: bd.id, displayName: bd.displayName, aggregates: [aggregate] });
      });
      setFilters(grouped);
    } else {
      setFilters([makeBlankRowBuilder()]);
    }
    /* Keyed on the data itself, not on `props`. `props` is a new object on every render,
       so this ran constantly and overwrote the panel from the applied config - a row you
       had just added or removed reappeared/vanished before you could press Apply.
       eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    props?.selectedResource?.id,
    JSON.stringify(props?.selectedResource?.availableFields || []),
    JSON.stringify(props?.selectedResource?.aggRowBuilders || []),
    JSON.stringify(props?.selectedResource?.aggregation?.row || []),
  ]);

  // return (
  //   <div style={{ overflowY: "auto", height: "30vh" }}>
  //     {props.activeWidgetName === "table" ? (
  //       <>
  //         <Typography.Title level={4}>Row Buildessr</Typography.Title>
  //         <div style={{ display: "flex", alignItems: "center" }}>
  //           {returnDataArrayObject(props.reportDataObject) ? (
  //             <div style={{ width: "100%" }}>
  //               {filters.map((fi) => {
  //                 return (
  //                   <Row key={"fil_" + fi.id} gutter={16} style={{ marginTop: "16px" }}>
  //                     <Col span={6}>
  //                       <div>Display Name:</div>
  //                       <div>
  //                         <Input
  //                           onChange={(e) => updateFilter(fi, "displayName", e.target.value)}
  //                           value={fi.displayName}
  //                         />
  //                       </div>
  //                     </Col>

  //                     <Col span={6}>
  //                       <div>Aggregate Type:</div>
  //                       <div>
  //                         <Select
  //                           style={{ width: "90%" }}
  //                           placeholder="Select Data Field"
  //                           defaultValue={"sum"}
  //                           onChange={(val) => updateFilter(fi, "aggType", val)}
  //                         >
  //                           <Option value="sum" key="sum">
  //                             Sum
  //                           </Option>
  //                           <Option value="average" key="average">
  //                             Average
  //                           </Option>
  //                           <Option value="minimum" key="minimum">
  //                             Minimum
  //                           </Option>
  //                           <Option value="maximum" key="maximum">
  //                             Maximum
  //                           </Option>
  //                           <Option value="count" key="count">
  //                             Count
  //                           </Option>
  //                           <Option value="standarddeviation" key="standarddeviation">
  //                             Standard deviation
  //                           </Option>
  //                           <Option value="variance" key="variance">
  //                             Variance
  //                           </Option>
  //                           <Option value="median" key="median">
  //                             Median
  //                           </Option>
  //                         </Select>
  //                       </div>
  //                     </Col>

  //                     <Col span={8}>
  //                       <div>Field Name:</div>
  //                       <div>
  //                         <Select
  //                           showSearch
  //                           style={{ width: "90%" }}
  //                           placeholder="Select Data Field"
  //                           value={fi.fieldName}
  //                           onChange={(val) => updateFilter(fi, "fieldName", val)}
  //                         >
  //                           {props.selectedResource &&
  //                             props.selectedResource.availableFields &&
  //                             fields?.map((af, j) => {
  //                               return (
  //                                 <Option value={af.fieldName} key={fi.id + "_" + j}>
  //                                   {af.fieldName}
  //                                 </Option>
  //                               );
  //                             })}
  //                         </Select>
  //                       </div>
  //                     </Col>

  //                     <Col span={1}>
  //                       <div>&nbsp;</div>
  //                       <div>
  //                         <Button type="text" icon={<CloseOutlined />} onClick={() => deleteFilterItem(fi)} />
  //                       </div>
  //                     </Col>
  //                   </Row>
  //                 );
  //               })}

  //               <Row style={{ marginTop: "16px", marginBottom: "32px" }} gutter={16}>
  //                 <Col span={4}>
  //                   <Button onClick={setFilterItem}>Add New +</Button>
  //                 </Col>
  //                 <Col span={4}>
  //                   <Button type="primary" onClick={() => applyFilters(filters)}>
  //                     Apply
  //                   </Button>
  //                 </Col>
  //               </Row>
  //             </div>
  //           ) : (
  //             <div>
  //               <NotFound />
  //             </div>
  //           )}
  //         </div>

  //         <hr />
  //       </>
  //     ) : null}

  //     <Typography.Title level={4}>Column Builder</Typography.Title>
  //     <div>
  //       <ColBuilder
  //         reportDataObject={props.reportDataObject}
  //         selectedResource={props.selectedResource}
  //         availableFields={fields}
  //         updateAggRowBuilders={updateAggRowBuilders}
  //         resetBuilders={clearBuilder}
  //       />
  //     </div>
  //   </div>
  // );
  return (
    <div className="data-builder-wrapper">
      <div className="builder-tab-group">
        <button
          className={`builder-tab-btn ${activeTab === "row" ? "active" : ""}`}
          onClick={() => setActiveTab("row")}
        >
          Row Builder
        </button>
        <button
          className={`builder-tab-btn ${activeTab === "col" ? "active" : ""}`}
          onClick={() => setActiveTab("col")}
        >
          Column Builder
        </button>
      </div>

      {/* ── ROW BUILDER ── */}
      {/* {activeTab === "row" && props.activeWidgetName === "table" ? (
        <div>
          {returnDataArrayObject(props.reportDataObject) ? (
            <>
              <div className="builder-rows-container">
                {filters.map((fi) => (
                  <div key={"fil_" + fi.id} className="builder-row">
                    <div className="builder-row-grid">
                      <div className="builder-field">
                        <label>Display Name</label>
                        <Input
                          placeholder="Name"
                          value={fi.displayName}
                          onChange={(e) => updateFilter(fi, "displayName", e.target.value)}
                        />
                      </div>
                      <div className="builder-field">
                        <label>Aggregate Type</label>
                        <Select
                          value={fi.aggType}
                          defaultValue="sum"
                          onChange={(val) => updateFilter(fi, "aggType", val)}
                        >
                          <Option value="sum">Sum</Option>
                          <Option value="average">Average</Option>
                          <Option value="minimum">Minimum</Option>
                          <Option value="maximum">Maximum</Option>
                          <Option value="count">Count</Option>
                          <Option value="standarddeviation">Std Dev</Option>
                          <Option value="variance">Variance</Option>
                          <Option value="median">Median</Option>
                        </Select>
                      </div>
                      <div className="builder-field">
                        <label>Field Name</label>
                        <Select
                          showSearch
                          placeholder="Select"
                          value={fi.fieldName}
                          onChange={(val) => updateFilter(fi, "fieldName", val)}
                        >
                          {fields?.map((af, j) => (
                            <Option value={af.fieldName} key={fi.id + "_" + j}>
                              {af.fieldName}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button type="text" className="filter-remove-btn " onClick={() => deleteFilterItem(fi)}>
                        <img src={DeleteIcon} alt="delete-icon" /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button type="link" className="builder-add-btn" onClick={setFilterItem}>
                + Add New
              </Button>
            </>
          ) : (
            <NotFound />
          )}
        </div>
      ) : null} */}

      {activeTab === "row" && props.activeWidgetName === "table" ? (
        <div>
          {returnDataArrayObject(props.reportDataObject) ? (
            <>
              <div className="builder-rows-container">
                {filters.map((fi, filterIndex) => (
                  <div key={"fil_" + fi.id} className="builder-card">
                    {/* Display Name */}
                    <div className="builder-field" style={{ marginBottom: "8px" }}>
                      <label>Display Name</label>
                      <Input
                        placeholder="Name"
                        value={fi.displayName}
                        onChange={(e) => updateFilterField(fi.id, "displayName", e.target.value)}
                      />
                    </div>

                    {/* Aggregate lines.
                        One builder row = one summary row = ONE aggregate type. The first
                        line sets the function; the rest follow it and are locked, so a
                        different function means a new builder row ("+ Add New"). */}
                    {fi.aggregates.map((agg, idx) => (
                      <div key={agg.id} className="builder-agg-row">
                        <div className="builder-field">
                          <label>Aggregate Type</label>
                          <Select
                            value={idx === 0 ? agg.aggType : fi.aggregates[0].aggType}
                            disabled={idx > 0}
                            onChange={(val) => updateAggregate(fi.id, agg.id, "aggType", val)}
                          >
                            <Option value="sum">Sum</Option>
                            <Option value="average">Average</Option>
                            <Option value="minimum">Minimum</Option>
                            <Option value="maximum">Maximum</Option>
                            <Option value="count">Count</Option>
                            <Option value="standarddeviation">Std Dev</Option>
                            <Option value="variance">Variance</Option>
                            <Option value="median">Median</Option>
                          </Select>
                        </div>
                        <div className="builder-field">
                          <label>Field Name</label>
                          <Select
                            showSearch
                            placeholder="Select"
                            value={agg.fieldName || undefined}
                            onChange={(val) => updateAggregate(fi.id, agg.id, "fieldName", val)}
                          >
                            {/* a column can only be aggregated once within the same row */}
                            {fields
                              ?.filter(
                                (af) =>
                                  af.fieldName === agg.fieldName ||
                                  !fi.aggregates.some((other) => other.fieldName === af.fieldName),
                              )
                              .map((af, j) => (
                                <Option value={af.fieldName} key={agg.id + "_" + j}>
                                  {af.fieldName}
                                </Option>
                              ))}
                          </Select>
                        </div>
                        {/* Remove aggregate — only show if more than one */}
                        {fi.aggregates.length > 1 ? (
                          <Button
                            type="text"
                            className="builder-agg-remove"
                            onClick={() => removeAggregate(fi.id, agg.id)}
                          >
                            <img src={DeleteIcon} alt="delete" style={{ width: "15px", height: "17px" }} />
                          </Button>
                        ) : null}
                      </div>
                    ))}

                    {/* Add Aggregate + Remove whole row */}
                    <div className="builder-card-footer">
                      <Button type="link" className="builder-add-btn" onClick={() => addAggregate(fi.id)}>
                        + Add Aggregate
                      </Button>
                      {/* The first builder always stays - Remove appears from the second
                          one onward, so the panel can never be emptied by accident. */}
                      {filterIndex > 0 ? (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <Button className="filter-remove-btn" onClick={() => deleteFilterItem(fi)}>
                            <img src={DeleteIcon} alt="delete-icon" /> Remove
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              <Button type="link" className="builder-add-btn add-new-btn-filter" onClick={setFilterItem}>
                + Add New
              </Button>
            </>
          ) : (
            <NotFound />
          )}
        </div>
      ) : null}

      {/* ── COLUMN BUILDER ── */}
      {activeTab === "col" ? (
        <ColBuilder
          reportDataObject={props.reportDataObject}
          selectedResource={props.selectedResource}
          availableFields={fields}
          updateAggRowBuilders={updateAggRowBuilders}
          onBuildersChange={setColBuilders}
        />
      ) : null}

      {/* ── SINGLE APPLY/RESET for both tabs ── */}
      {/* One Apply serves both tabs, so it has to dispatch the builder for the tab in
          view - previously it always sent row builders, so Column Builder never applied. */}
      <div className="builder-actions">
        <Button type="primary" onClick={applyActiveBuilders}>
          Apply
        </Button>
        <Button onClick={resetActiveBuilders}>Reset</Button>
      </div>
    </div>
  );
}
